import React from 'react';
import {
  Box,
  Grid,
  Typography,
  Button,
  TextField,
  Dialog,
  Select,
  MenuItem,
  Divider,
  CircularProgress,
  Chip,
  SelectChangeEvent
} from '@mui/material';
import { ApiStatus } from '../../utils/api.types';
import { IGSTPaymentForm } from '../../utils/gstSettlement.types';

interface GSTPaymentDialogProps {
  open: boolean;
  onClose: () => void;
  paymentForm: IGSTPaymentForm;
  onPaymentFormChange: (field: string, value: any) => void;
  onReturnDetailsChange: (field: string, value: any) => void;
  onSubmit: () => void;
  paymentStatus: ApiStatus;
  bankLedgers: any[];
}

const GSTPaymentDialog: React.FC<GSTPaymentDialogProps> = ({
  open,
  onClose,
  paymentForm,
  onPaymentFormChange,
  onReturnDetailsChange,
  onSubmit,
  paymentStatus,
  bankLedgers
}) => {
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 2
    }).format(amount);
  };

  // Get available return types based on taxpayer type
  const getAvailableReturnTypes = () => {
    const taxpayerType = paymentForm.returnDetails.taxpayerType;

    if (taxpayerType === 'REGULAR') {
      return [
        { value: 'GSTR-1', label: 'GSTR-1 (Sales)' },
        { value: 'GSTR-3B', label: 'GSTR-3B (Monthly Return)' },
        { value: 'GSTR-9', label: 'GSTR-9 (Annual Return)' },
        { value: 'GSTR-9C', label: 'GSTR-9C (Reconciliation)' }
      ];
    } else if (taxpayerType === 'QRMP') {
      return [
        { value: 'PMT-06', label: 'PMT-06 (Monthly Payment)' },
        { value: 'GSTR-1', label: 'GSTR-1 (Quarterly Sales)' },
        { value: 'GSTR-3B', label: 'GSTR-3B (Quarterly Return)' }
      ];
    } else if (taxpayerType === 'COMPOSITION') {
      return [
        { value: 'CMP-08', label: 'CMP-08 (Quarterly Return)' },
        { value: 'GSTR-4', label: 'GSTR-4 (Annual Return)' }
      ];
    }
    return [];
  };

  // Get available payment frequencies based on taxpayer type and return type
  const getAvailableFrequencies = () => {
    const taxpayerType = paymentForm.returnDetails.taxpayerType;
    const returnType = paymentForm.returnDetails.returnType;

    if (taxpayerType === 'REGULAR') {
      if (returnType === 'GSTR-9' || returnType === 'GSTR-9C') {
        return [{ value: 'YEARLY', label: 'Yearly' }];
      }
      return [{ value: 'MONTHLY', label: 'Monthly' }];
    } else if (taxpayerType === 'QRMP') {
      if (returnType === 'PMT-06') {
        return [{ value: 'MONTHLY', label: 'Monthly' }];
      }
      return [{ value: 'QUARTERLY', label: 'Quarterly' }];
    } else if (taxpayerType === 'COMPOSITION') {
      if (returnType === 'GSTR-4') {
        return [{ value: 'YEARLY', label: 'Yearly' }];
      }
      return [{ value: 'QUARTERLY', label: 'Quarterly' }];
    }
    return [{ value: 'MONTHLY', label: 'Monthly' }];
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
      <Box>
        {/* Header with buttons */}
        <Grid container>
          <Grid item xs={12} p={2} pl={2} bgcolor='#e9ecf1' style={{ borderTopLeftRadius: 8, borderTopRightRadius: 8, height: 50 }}>
            <div style={{ float: 'right', marginTop: -7, marginRight: -5 }}>
              <Button
                variant="contained"
                onClick={onSubmit}
                disabled={
                  paymentStatus === ApiStatus.LOADING ||
                  !paymentForm.challanNumber ||
                  !paymentForm.bankAccount ||
                  (paymentForm.cgstPaid === 0 && paymentForm.sgstPaid === 0 && paymentForm.igstPaid === 0)
                }
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                size='small'
              >
                {paymentStatus === ApiStatus.LOADING ? <CircularProgress size={20} /> : 'Record Payment'}
              </Button>
              <Button
                style={{ color: '#488dff', backgroundColor: '#fff', border: '1px solid #488dff', marginRight: 10 }}
                variant="contained"
                size='small'
                onClick={onClose}
              >
                Cancel
              </Button>
            </div>
            <h3 style={{ marginTop: -2 }}>Record GST Payment to Government</h3>
          </Grid>
        </Grid>

        {/* Form Content */}
        <Grid container p={2} spacing={2}>
          {/* Return Details */}
          <Grid item xs={12} mt={1}>
            <Divider style={{ marginRight: -8 }}>
              <Chip label="GST Return Details" size="small" />
            </Divider>
          </Grid>

          <Grid item xs={4} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Taxpayer Type*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.returnDetails.taxpayerType}
              onChange={(e: SelectChangeEvent<any>) => onReturnDetailsChange('taxpayerType', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              <MenuItem value="REGULAR">Regular</MenuItem>
              <MenuItem value="QRMP">QRMP (Quarterly Return Monthly Payment)</MenuItem>
              <MenuItem value="COMPOSITION">Composition</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={4}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Return Type*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.returnDetails.returnType}
              onChange={(e: SelectChangeEvent<any>) => onReturnDetailsChange('returnType', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              {getAvailableReturnTypes().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={4} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Payment Frequency*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.returnDetails.paymentFrequency}
              onChange={(e: SelectChangeEvent<any>) => onReturnDetailsChange('paymentFrequency', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              {getAvailableFrequencies().map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          {/* GST Amounts */}
          <Grid item xs={12} mt={1}>
            <Divider style={{ marginRight: -8 }}>
              <Chip label="GST Amounts" size="small" />
            </Divider>
          </Grid>

          <Grid item xs={4} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>CGST Paid</h5>
            <TextField
              placeholder='CGST Paid'
              className='c-text-field-4'
              type="number"
              value={paymentForm.cgstPaid}
              onChange={(e) => onPaymentFormChange('cgstPaid', parseFloat(e.target.value) || 0)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={4}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>SGST Paid</h5>
            <TextField
              placeholder='SGST Paid'
              className='c-text-field-4'
              type="number"
              value={paymentForm.sgstPaid}
              onChange={(e) => onPaymentFormChange('sgstPaid', parseFloat(e.target.value) || 0)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={4} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>IGST Paid</h5>
            <TextField
              placeholder='IGST Paid'
              className='c-text-field-4'
              type="number"
              value={paymentForm.igstPaid}
              onChange={(e) => onPaymentFormChange('igstPaid', parseFloat(e.target.value) || 0)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mt: 1, color: '#1976d2', fontWeight: 600 }}>
              Total Amount: {formatCurrency(paymentForm.cgstPaid + paymentForm.sgstPaid + paymentForm.igstPaid)}
            </Typography>
          </Grid>

          {/* Payment Details */}
          <Grid item xs={12} mt={1}>
            <Divider style={{ marginRight: -8 }}>
              <Chip label="Payment Details" size="small" />
            </Divider>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Payment Date*</h5>
            <TextField
              placeholder='Payment Date'
              className='c-text-field-4'
              type="date"
              value={paymentForm.paymentDate}
              onChange={(e) => onPaymentFormChange('paymentDate', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              required
              size='small'
              InputProps={{
                disableUnderline: true
              }}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Challan Number*</h5>
            <TextField
              placeholder='Challan Number'
              className='c-text-field-4'
              value={paymentForm.challanNumber}
              onChange={(e) => onPaymentFormChange('challanNumber', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              required
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Payment Mode*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.paymentMode}
              onChange={(e: SelectChangeEvent<any>) => onPaymentFormChange('paymentMode', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px'
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              <MenuItem value="NEFT">NEFT</MenuItem>
              <MenuItem value="RTGS">RTGS</MenuItem>
              <MenuItem value="UPI">UPI</MenuItem>
              <MenuItem value="NETBANKING">Net Banking</MenuItem>
              <MenuItem value="CHEQUE">Cheque</MenuItem>
            </Select>
          </Grid>

          <Grid item xs={6} pl={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Bank Account*</h5>
            <Select
              className='c-text-field-4'
              value={paymentForm.bankAccount}
              onChange={(e: SelectChangeEvent<any>) => onPaymentFormChange('bankAccount', e.target.value)}
              disableUnderline
              required
              MenuProps={{
                PaperProps: {
                  sx: {
                    border: "1.5px solid #e9ecf1",
                    boxShadow: 'none',
                    borderRadius: '5px',
                    maxHeight: 300
                  }
                }
              }}
              variant='outlined'
              sx={{
                width: '100%',
                boxShadow: "none",
                paddingTop: '0px !important',
                borderRadius: '7px !important',
                marginTop: '4px',
                '& .MuiOutlinedInput-input': { p: 0.7 },
                marginBottom: '3px',
                ".MuiOutlinedInput-notchedOutline": { border: 0 },
                "&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
                "&.MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  border: 0,
                },
              }}
            >
              {bankLedgers.length === 0 ? (
                <MenuItem disabled value="">
                  No bank accounts found
                </MenuItem>
              ) : (
                bankLedgers.map((ledger: any) => (
                  <MenuItem key={ledger._id} value={ledger._id}>
                    {ledger.ledgerName}
                  </MenuItem>
                ))
              )}
            </Select>
          </Grid>

          <Grid item xs={6} pr={1}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Reference Number (Optional)</h5>
            <TextField
              placeholder='Reference Number'
              className='c-text-field-4'
              value={paymentForm.referenceNumber}
              onChange={(e) => onPaymentFormChange('referenceNumber', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <h5 style={{ fontWeight: 'normal', color: '#9da7b7', fontSize: 14 }}>Remarks (Optional)</h5>
            <TextField
              placeholder='Remarks'
              className='c-text-field-4'
              multiline
              rows={3}
              value={paymentForm.remarks}
              onChange={(e) => onPaymentFormChange('remarks', e.target.value)}
              variant="standard"
              sx={{ width: '100%', mt: 0.5, borderRadius: '8px!important' }}
              size='small'
              InputProps={{
                disableUnderline: true
              }}
            />
          </Grid>
        </Grid>
      </Box>
    </Dialog>
  );
};

export default GSTPaymentDialog;
